import React, { useEffect, useMemo, useState } from 'react';
import { Tag } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SortOrder } from 'antd/es/table/interface';
import { RequestData } from '@ant-design/pro-table';
import { ProColumns } from '@ant-design/pro-table/es/typing';
import clsx from 'clsx';
import dayjs from 'dayjs';
import Table from '../../Common/Table';
import { UserTableRow, useUsersTableGet } from '../../../hooks/api/users';
import { createDefaultHiddenColumns, getSorterParams, queryFilterParams } from '../../../utils';
import { useMessageError } from '../../../hooks/common';
import { useAuth, UserStatusType } from '../../../store/auth';
import { UserRole, UserRoleDisplay, UserStatusDisplay } from '../../../enums/user';
import { dateTimeFormat } from '../../../contstant';
import {
  DeactivateButton, DeleteButton, InviteUserButton, ResetPasswordButton,
} from './actions';
import CsvExportButton from './CsvExportButton';

export enum UserStatusColor {
  'active' = 'green',
  // 'pending' = 'blue',
  'pending' = 'processing',
  'deactivated' = 'red',
  'archived' = 'default',
}

export function getUserStatusColor(status: UserStatusType): string {
  // @ts-ignore take a color from the list
  return UserStatusColor[status?.toLowerCase()] || 'rgba(0,0,0,.15)';
}

const columns: ProColumns<UserTableRow>[] = [
  /* {
    title: 'Name',
    dataIndex: 'name',
    key: 'name',
    sorter: true,
    renderText: (_, row) => row.fullName,
  }, */
  {
    title: 'Email',
    dataIndex: 'email',
    key: 'email',
    sorter: true,
  },
  {
    title: 'Has mobile app',
    dataIndex: 'hasMobileApp',
    key: 'hasMobileApp',
    sorter: false,
    valueEnum: {
      true: 'Yes',
      false: 'No',
    },
  },
  {
    title: 'Is private',
    dataIndex: 'isPrivate',
    key: 'isPrivate',
    sorter: false,
    valueEnum: {
      true: 'Yes',
      false: 'No',
    },
  },
  /* {
    title: 'Phone',
    dataIndex: 'phone',
    key: 'phone',
    renderText: (text) => (text ? formatPhoneNumberIntl(text) : '-'),
  }, */
  {
    title: 'Role',
    dataIndex: 'role',
    key: 'roles', // key - for filters. dataIndex - for display.
    sorter: true,
    valueEnum: UserRoleDisplay,
  },
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    sorter: true,
    valueEnum: UserStatusDisplay,
    render: (_, record) => (
      <Tag color={getUserStatusColor(record.status)} className="capitalize-first-letter">{record.status}</Tag>
    ),
  },
  /* {
    title: 'Date of birth',
    dataIndex: 'dateOfBirth',
    key: 'dateOfBirthdateOfBirth',
    hideInSearch: true,
    // valueType: 'dateRange',
    sorter: false,
    // .utc(true)
    renderText: (text) => (dayjs(text).isValid() ? dayjs(text).format(dateFormat) : '-'),
  }, */
  {
    title: 'Created at',
    dataIndex: 'createdAt',
    key: 'createdAt',
    hideInSearch: true,
    sorter: true,
    renderText: (text) => dayjs(text).format(dateTimeFormat),
  },
  {
    title: 'Updated at',
    dataIndex: 'updatedAt',
    key: 'updatedAt',
    hideInSearch: true,
    sorter: true,
    renderText: (text) => dayjs(text).format(dateTimeFormat),
  },
];

function ToolBar() {
  return [
    <CsvExportButton key="export-users" />,
    /* <Link key="create" to="create">
      <Button
        key="add"
        icon={<PlusCircleOutlined />}
        type="primary"
        onClick={(e) => {
          // e.preventDefault();
          // navigate('create');
        }}
      >
        Add
      </Button>
    </Link>, */
  ];
}

export default function Users(): React.ReactNode | null {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = !!user?.role && [UserRole.ADMIN, UserRole.ROOT].includes(user.role);
  const usersGet = useUsersTableGet();
  const [, setSearchParams] = useSearchParams();
  const [tableKey, setTableKey] = useState<number>(Date.now());

  /** Re-render table once user?.role is resolved (e.g. after /users/me finishes), so actions column is correct. */
  useEffect(() => {
    setTableKey(Date.now());
  }, [user?.role]);

  const tableRequest = (
    {
      current,
      pageSize,
      ...args
    }: Record<string, string> & {
      pageSize?: number | undefined;
      current?: number | undefined;
      keyword?: string | undefined;
    },
    sorter: Record<string, SortOrder>,
  ): Promise<Partial<RequestData<UserTableRow>>> => {
    const newParams = queryFilterParams({
      page: current ? `${current}` : '1',
      pageSize: pageSize ? `${pageSize}` : '10',
      ...args,
      ...getSorterParams(sorter),
    });

    setSearchParams({ ...args, ...getSorterParams(sorter) }, { replace: true });

    return usersGet.fetch({
      ...newParams,
      roles: newParams.roles ? [newParams.roles] : undefined,
    }).then((response) => {
      if (response) {
        const { data, total } = response;

        return ({
          data: (data || []).map(((item) => ({
            ...item,
            key: item.id,
          }))),
          success: true,
          total,
        });
      }

      return ({ data: [], success: false, total: 0 });
    });
  };

  useMessageError([usersGet]);

  const defaultHiddenColumns = useMemo(() => createDefaultHiddenColumns(
    ['createdAt', 'updatedAt'],
  ), []);

  return (
    <Table<UserTableRow>
      key={tableKey}
      resetKey={() => setTableKey(Date.now())}
      className={clsx('transparent')}
      headerTitle="List of users"
      toolBarRender={isAdmin ? ToolBar : undefined}
      request={tableRequest}
      columns={columns}
      actions={user?.role && [UserRole.MITARBEITER, UserRole.ADMIN, UserRole.ROOT].includes(user?.role) ? [
        ['edit', ({ record }: { record: UserTableRow }) => ({
          title: isAdmin ? 'Edit administrator' : 'View user',
          ...(!isAdmin ? { icon: <EyeOutlined /> } : {}),
          onClick: (e: MouseEvent) => {
            e.preventDefault();
            navigate(`${record.id}/edit`);
          },
          /** If user is not 'root' - restrict from editing ROOT and ADMIN users. */
          disabled: isAdmin && [UserRole.ADMIN, UserRole.ROOT].includes(record.role) && user?.role !== UserRole.ROOT,
          style: isAdmin && [UserRole.ADMIN, UserRole.ROOT].includes(record.role) && user?.role !== UserRole.ROOT
            ? { visibility: 'hidden' } : undefined,
        })],
        [DeleteButton, {
          setTableKey,
        }],
        [DeactivateButton, { setTableKey }],
        [ResetPasswordButton],
        [InviteUserButton, { setTableKey }],
      ].filter((action) => isAdmin || action[0] === 'edit') as any : undefined}
      search={{
        searchText: 'Filter',
        resetText: 'Clear',
        style: {
          // display: 'none',
        },
      }}
      options={{
        // search: {
        //   name: 'search',
        // },
      }}
      columnsState={{
        persistenceKey: 'pro-table-users',
        persistenceType: 'localStorage',
        defaultValue: defaultHiddenColumns,
      }}
    />
  );
}
